import React, { useCallback, useState, useLayoutEffect } from 'react';
import { Typography, Link, Button, AppBar, Toolbar, Box } from '@mui/material';
import Cookies from 'js-cookie';
import { loadMixpanel, loadMouseflow, loadNewRelicBrowser, loadGoogleAnalytics } from '../../tracking/loaders';
import { PublicVendorProfile } from '@trnsact/trnsact-shared-types';


interface Props {
  vendorProfile: PublicVendorProfile
}

const CONSENT_COOKIE_NAME = 'CookieConsent';
const CONSENT_COOKIE_EXPIRES_DAYS = 365;

async function loadAllTrackers() {
  await Promise.allSettled([loadMouseflow(), loadNewRelicBrowser(), loadMixpanel(), loadGoogleAnalytics()]);
}

export const CookieConsentManager = ({ vendorProfile }: Props) => {

  const [isCookieConsentSet, setIsCookieConsentSet] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!vendorProfile.ocaShowCookieConsentBanner) {
      // if ocaShowCookieConsentBanner setting is false, just load all trackers, banner won't show (regular behavior)
      loadAllTrackers();
    } else {
      // otherwise, check cookie
      const value = Cookies.get(CONSENT_COOKIE_NAME);
      if (value === 'true') {
        loadAllTrackers();
      }
      setIsCookieConsentSet(!!value);
    }
  }, []);

  const handleAccept = () => {
    Cookies.set(CONSENT_COOKIE_NAME, 'true', { expires: CONSENT_COOKIE_EXPIRES_DAYS });
    loadAllTrackers();
    setIsCookieConsentSet(true);
  };

  const handleDecline = () => {
    removeTrackerCookies();
    Cookies.set(CONSENT_COOKIE_NAME, 'false', { expires: CONSENT_COOKIE_EXPIRES_DAYS });
    setIsCookieConsentSet(true);
  };

  const removeTrackerCookies = () => {
    const all = Cookies.get();
    Object.keys(all)
      /* .filter((name) => name.startsWith('_ga') || name.startsWith('mf_') || name.startsWith('mp_')) */
      .forEach((name) => {
        Cookies.remove(name);
        Cookies.remove(name, { path: '/', domain: '.trnsact.com' });
      });
  };

  if (isCookieConsentSet || !vendorProfile.ocaShowCookieConsentBanner) {
    return null;
  }

  return (
    <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: '#333' }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body1" component="span">
            This website uses cookies to enhance the user experience.
          </Typography>{' '}
          <Link href="https://trnsact.com/privacy-policy/" target="_blank" fontWeight="bold">
            Privacy Policy
          </Link>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
            onClick={handleDecline}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccept}
          >
            Accept
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
